// The key to an asset's player, for a member who can open it.

import { api, withQuery } from '../client'
import type { components } from '../schema'

export type AssetPlaybackOpened = components['schemas']['AssetPlaybackOpened']

/**
 * A `playback_token` for one asset, to present to the `visitor` playback routes. 404 for an asset
 * this caller cannot open, whatever the reason. The token names no publication: what it plays is
 * never withheld, opens no analytics session and records no resume point. It reaches that asset and
 * nothing past it, so a collection's entries are each opened here. `viaAssetId`: as `getAsset`'s.
 */
export function openAssetPlayback(assetId: string, viaAssetId?: string): Promise<AssetPlaybackOpened> {
  return api.post<AssetPlaybackOpened>(
    withQuery(`/assets/${encodeURIComponent(assetId)}/playback`, { via_asset_id: viaAssetId }),
  )
}

export type AssetPlayerView = components['schemas']['AssetPlayerView']

/**
 * How the asset's player looks and the cards it shows, as a publication of it would: the resolved
 * preset over the watermark, and the switched-on cards of its three scopes — less the advertising and
 * the recommendations strip, which a member's player does not carry.
 *
 * `preset` previews another preset first in the cascade — what a code carrying `preset=` draws, or
 * a native embed's own choice; one the page would not admit answers the cascade's default.
 */
export function getAssetPlayer(assetId: string, signal?: AbortSignal, preset?: string): Promise<AssetPlayerView> {
  return api.get<AssetPlayerView>(
    withQuery(`/assets/${encodeURIComponent(assetId)}/player`, { preset }),
    undefined,
    signal,
  )
}
